import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FormControl as BootstrapFormControl } from "react-bootstrap";
import ReactInputMask from "react-input-mask";
import Text from "../Text";
import ReactCurrencyInput from "react-currency-input";

import * as colors from "../../assets/_base/colors";

// Icon styling
const Icon = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: ${colors.primary};

  i {
    display: flex;
    position: absolute;
    margin-left: -20px;
    cursor: pointer;
  }
`;

// Default styling for both input types
const defaultInputStyle = `
  border-radius: 3px;
  border: 1px solid ${colors.primary};
  color: ${colors.primary};

  :focus {
    border-color: ${colors.primary};
  }
  ::placeholder {
    color: ${colors.secondary};
    opacity: .9;
  }
  :disabled {
    background-color: ${colors.gray};
  }
  :disabled::placeholder {
    color: ${colors.gray};
  }
`;

const FormControl = styled(BootstrapFormControl)`
  ${defaultInputStyle}
`;

const InputMask = styled(ReactInputMask)`
  ${defaultInputStyle}
`;

const Currency = styled(ReactCurrencyInput)`
  ${defaultInputStyle};
  padding-top: 6px;
  padding-bottom: 6px;
  font-size: 14px;
`;

/**
 * A customizable input component supporting various types including masked input, text areas, select, and password fields.
 *
 * @description
 * - Renders a text, password, masked, or select input, optionally showing an icon to toggle password visibility.
 * - Can be configured to display a label and a "rightLabel" element (e.g., action link or help text).
 * - Displays a loading spinner or error state when needed.
 *
 * @param {Object} props Component properties.
 * @param {string} [props.as]   - Determines the element to render ("select", "textarea", etc.). Defaults to a standard text input.
 * @param {boolean} [props.disabled=false] - Disables the input if true.
 * @param {string} [props.label] - Main label text shown above the input.
 * @param {React.ReactNode} [props.rightLabel] - Optional node shown to the right of the label (e.g., link or subtitle).
 * @param {string} [props.type] - Input type for both standard and masked inputs (e.g., "text", "password", "cpf", "cnpj", "phone_number").
 * @param {string} [props.name] - Name attribute of the input element.
 * @param {string|number} [props.value] - Current value of the input.
 * @param {Function} [props.onChange] - Handler called whenever the input value changes.
 * @param {string} [props.error] - If provided, indicates an error state and displays styling accordingly.
 * @param {boolean} [props.loading=false] - Shows a loading spinner overlay on the right side if true.
 * @param {boolean} [props.nogroup=false] - If true, skips wrapping the field in a "form-group" div.
 *
 * @example
 * <CustomInput
 *   label="Email"
 *   name="userEmail"
 *   type="text"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   error={hasError ? "Invalid email" : ""}
 * />
 */
function CustomInput(props) {
  const [mask, setMask] = useState("");
  const [eyeSlash, setEyeSlash] = useState(true);

  // Set the mask if the input type is one of the supported masks (cpf, cnpj, phone_number)

  function readMaskProps() {
    if (props.type) {
      if (["cpf", "cnpj", "phone_number"].includes(props.type)) {
        if (props.type === "cpf") {
          setMask("999.999.999-99");
        } else if (props.type === "cnpj") {
          setMask("99.999.999/9999-99");
        } else if (props.type === "phone_number") {
          setMask("(99) 99999-9999");
        }
      }
    }
  }

  useEffect(readMaskProps, [props.type]);

  // Toggle password visibility when the eye icon is clicked
  function togglePasswordType(e) {
    const input = e.target.parentElement.parentElement.querySelector("input");
    if (input.type === "text") {
      input.type = "password";
      setEyeSlash(true);
    } else {
      input.type = "text";
      setEyeSlash(false);
    }
    input.focus();
  }

  const inputClassName = `form-control ${props.error ? "is-invalid" : ""}`;

  // Conditionally render either a standard/textarea/select input or a masked input

  const renderInput = () => {
    const { as, onChange, loading, disabled, name, value } = props;

    if (props.type === "currency") {
      return (
        <Currency
          prefix="R$ "
          className="form-control"
          decimalSeparator=","
          thousandSeparator="."
          precision="2"
          onChange={(str, value ,e) =>{
            onChange({target: {
              name: name,
              value: value
            }})
          }}
          name={name}
          value={value}
          disabled={disabled || loading}
        />
      );
    } else if (mask === "" || as === "select" || as === "textarea") {
      return (
        <FormControl
        {...props}
          className={inputClassName}
          disabled={loading || disabled}
          onChange={onChange}
        />
      );
    } else {
      return (
        <InputMask
          mask={mask}
          name={name}
          value={value}
          className={inputClassName}
          disabled={loading || disabled}
          onChange={onChange}
        />
      );
    }
  };

  return (
    <div className={`${props.nogroup ? "" : "form-group"}`}>
      {props.label && (
        <div className="form-label d-flex justify-content-between">
          <Text color="primary">{props.label}</Text>
          {props.rightLabel || <React.Fragment />}
        </div>
      )}

      <div className="d-flex" style={{ position: "relative" }}>
        {renderInput()}

        {props.type === "password" && !props.loading ? (
          <Icon>
            <i
              style={{ color: colors.primary }}
              onClick={togglePasswordType}
              className={`fas fa-eye${eyeSlash ? "-slash" : ""}`}
            />
          </Icon>
        ) : (
          <span />
        )}

        {props.loading ? (
          <Icon>
            <i className="fas fa-spinner fa-spin" />
          </Icon>
        ) : (
          <span />
        )}
      </div>

      {props.error && (
        <div className="text-danger" style={{ marginTop: 5 }}>
          {props.error}
        </div>
      )}
    </div>
  );
}

CustomInput.propTypes = {
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  disabled: PropTypes.bool,
  label: PropTypes.string,
  rightLabel: PropTypes.node,
  type: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  error: PropTypes.string,
  loading: PropTypes.bool,
  nogroup: PropTypes.bool,
};

CustomInput.defaultProps = {
  as: "input",
  disabled: false,
  loading: false,
  nogroup: false,
};

export default CustomInput;



// WEBPACK FOOTER //
// ./src/_components/LoadingInput/index.jsx